import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import {PageTitle} from "../../../../Components/Landmarks";
import {LayoutFullWidth} from "../../../../Components/Layout";
import {loanApi} from "../loanApi";
import {LoanQueueTable} from "./LoanQueueTable";
import {libraryQueueApi} from "../../../Library/libraryQueueApi";
import {LoanSearchFormView} from "./LoanSearchFormView";
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {getLoanStatusTextByValue} from "../loanUtility";
import {loanQueueRow} from "../../../Library/libraryFactory";
import {locations} from "../../../../Utilities/Location";
import {openInNewTab} from "../../../../Utilities/htmlUtilities";
import {ButtonLinkStyle} from "../../../../Components/Inputs";
import {GridContainer, GridCell} from "../../../../Components/Grid";
import {createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";

export const LoanManagementContainer = ({
                                            handleApiCall,
                                            handleSetLoanSearchCriteria,
                                            isLoading,
                                            loanSearchCriteria,
                                            systemType,
                                            systemTypeText,
                                        }: ContextProps) => {

    const [queue, setQueue] = useState<Record<string, unknown>[]>([]);
    const [dropDowns, setDropDowns] = useState<Record<string, unknown> | null>(null);
    const noResults = isNullOrUndefined(queue) || queue.length === 0;


    const formatQueueItem = (queuedItem: Record<string, unknown>) => {
        const {catalogFormat, catalogItem, copy} = queuedItem as { catalogFormat: Record<string, unknown>; catalogItem: Record<string, unknown>; copy: Record<string, unknown> };
        return loanQueueRow({...catalogFormat, ...catalogItem, ...queuedItem, ...copy});
    }

    const saveFormattedQueue = (rawQueue: Record<string, unknown>[]) => {
        const formattedQueue = rawQueue.map((qi: Record<string, unknown>) => formatQueueItem(qi));
        setQueue(formattedQueue);
    }

    const handleGetQueue = () => {

        handleApiCall(() => loanApi.getItemsReadyForCheckout({
            ...loanSearchCriteria,
            systemType: systemType
        }), (results: Record<string, unknown>[]) => saveFormattedQueue(results));
    }

    const handleExportLoansClick = () => {
        if (noResults) {
            alert("Search criteria has no results. Adjust your search and try again.")
        } else {
            handleApiCall(() => loanApi.exportLoansReport(loanSearchCriteria, true));
        }
    };


    const handleGetDropDowns = () => {
        handleApiCall(() => loanApi.getListsForDropDowns(systemType), (results: Record<string, unknown>) => setDropDowns(results));
    }

    const handlePackingList = (queueItemId: string) => {
        openInNewTab(locations.LOAN_PACKING_LIST.getLink(queueItemId).to);
    }

    const handleRemoveFromQueue = (queuedItemId: string) => {
        if (confirm(`This will remove the Patrons request from the Queue. Are you sure you want to continue?`))
            handleApiCall(() => libraryQueueApi.removeFromQueue(queuedItemId), handleGetQueue);
    }

    const handleLoanQueueItem = (queuedItemId: string, loanStatus: number) => {
        if (confirm(`Are you sure you wanted to proceed with updating the loan status?`)) {
            const statusText = getLoanStatusTextByValue(loanStatus + 1);
            handleApiCall(() => loanApi.loan(queuedItemId, statusText), handleGetQueue);
        }
    }

    useEffect(() => {
        handleGetQueue();
    }, [loanSearchCriteria]);


    useEffect(() => {
        handleGetDropDowns();
    }, []);

    if (isNullOrUndefined(dropDowns)) return;

    const patrons = createListFromArrayOfObjects(dropDowns.patrons as Record<string, unknown>[], "userId", "fullName");
    const shippingLocations = createListFromArrayOfObjects(dropDowns.shippingLocations as Record<string, unknown>[], "shippingLocationId", "name");
    const students = createListFromArrayOfObjects(dropDowns.students as Record<string, unknown>[], "studentId", "fullName");

    return <>
        <PageTitle h1={`Loan Management - ${systemTypeText}`}/>

        <LoanSearchFormView
            handleSetLoanSearchCriteria={handleSetLoanSearchCriteria}
            isLoading={isLoading}
            loanSearchCriteria={loanSearchCriteria}
            patrons={patrons}
            shippingLocations={shippingLocations}
            students={students}
        />

        <LayoutFullWidth className={`catalog`}>
            <header>
                <h2>Search Results</h2>
            </header>

            <GridContainer isReverseOrder hasMarginX hasMarginY className={`text-right`}>
                <GridCell>
                    <ButtonLinkStyle
                        isCollapsed
                        label={`Export Loans`}
                        name="btnExportLoans"
                        onClick={handleExportLoansClick}
                    />
                </GridCell>
            </GridContainer>
            {
                queue &&
                <LoanQueueTable
                    handleLoanQueueItem={handleLoanQueueItem}
                    handlePackingList={handlePackingList}
                    handleRemoveFromQueue={handleRemoveFromQueue}
                    handleSetLoanSearchCriteria={handleSetLoanSearchCriteria}
                    loanSearchCriteria={loanSearchCriteria}
                    queue={queue}
                />
            }
        </LayoutFullWidth>
    </>;
};
